import React, { Component, type ComponentType, type ReactNode } from 'react';
import { cssMap, styled as styled2 } from '@compiled/react';
import noop from 'lodash/noop';
import { v1 as generateUniqId } from 'uuid';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
// @ts-expect-error - TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'HeadCellType'. Did you mean to use 'import HeadCellType from "@atlaskit/dynamic-table"' instead? | TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'HeadType'. Did you mean to use 'import HeadType from "@atlaskit/dynamic-table"' instead?
import { DynamicTableStateless, type HeadCellType, type HeadType } from '@atlaskit/dynamic-table';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { AnnouncerV2 } from '@atlassian/jira-accessibility/src/ui/announcer-v2/index.tsx';
import { ASC } from '@atlassian/jira-common-constants/src/sort-directions.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import MountReporter from '../../../common/page-ready/mount-reporter/index.tsx';
import type {
	SpotlightCellTarget,
	OperationParams,
	ColumnAlignment,
} from '../../../model/index.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

// eslint-disable-next-line jira/react/no-class-components
class Table<
	TOperation,
	TSortField extends string,
	TChildEntityId = string,
	TEntity = undefined,
> extends Component<Props<TOperation, TSortField, TChildEntityId, TEntity>> {
	static defaultProps = {
		isLoading: false,
		entityIds: [],
		totalItems: 0,
		sortDirection: ASC,
		mountMetricKey: undefined,
		getSpotlightForCell: noop,
		TableMaskView: undefined,
		firstColumnHeadingAlignment: 'left',
		lastColumnHeadingAlignment: 'left',
		filterQuery: '',
		tableLabel: undefined,
		resultsAnnouncerMessage: messages.filterResultsAnnouncement,
		onSort: noop,
		onOperationRequested: noop,
	};

	state = {
		hasSortingChanged: false,
		shouldAnnounceResults: false,
		returnFocusTo: null,
	};

	componentDidUpdate(prevProps: Readonly<Props<TOperation, TSortField, TChildEntityId, TEntity>>) {
		if (
			prevProps.sortDirection !== this.props.sortDirection ||
			prevProps.sortField !== this.props.sortField
		) {
			this.state.hasSortingChanged = true;
		}

		if (!prevProps.isLoading && this.props.isLoading) {
			this.state.shouldAnnounceResults = !this.state.hasSortingChanged;
		} else if (prevProps.isLoading && !this.props.isLoading) {
			this.state.hasSortingChanged = false;
		}

		if (this.state.returnFocusTo && fg('blu-7774_fix_focus_bug_table_header_in_directories')) {
			// Find the index of the sort field that we should put focus on, then go through all the headers and find the one that matches the sort field index
			const sortFieldIndex = this.props.tableConfiguration.findIndex(
				(config) => config.sortField === this.state.returnFocusTo,
			);

			if (sortFieldIndex >= 0) {
				const allHeaders = globalThis.document.querySelectorAll('thead th');

				if (sortFieldIndex < allHeaders.length) {
					const targetHeader = allHeaders[sortFieldIndex];

					const sortButton = targetHeader.querySelector(
						'button[aria-roledescription="Sort button"]',
					);

					if (sortButton instanceof HTMLElement) {
						sortButton.focus();
					}
				}
			}
		}
	}

	/**
	 * We very much wish that `attrs` is an object of this type {| key: string, sortOrder: string |}
	 * but unfortunately @atlaskit/dynamic-table types do not give any guarantee of that
	 * so we should be handling mixed here.
	 */
	onSort = (attrs: unknown, analyticsEvent: undefined | UIAnalyticsEvent) => {
		// @ts-expect-error - TS2339 - Property 'key' does not exist on type 'unknown'. | TS2339 - Property 'sortOrder' does not exist on type 'unknown'.
		const { key, sortOrder } = attrs;
		const { onSort } = this.props;

		if (analyticsEvent) {
			fireUIAnalytics(analyticsEvent, 'tableHeader', { key });
		}

		onSort({
			sortField: key,
			sortDirection: sortOrder,
		});

		if (fg('blu-7774_fix_focus_bug_table_header_in_directories')) {
			this.setState({ returnFocusTo: key });
		}
	};

	render() {
		const {
			tableConfiguration,
			entityIds,
			totalItems,
			EmptyView,
			isLoading,
			operationInitializationStatusPerEntity,
			sortField,
			sortDirection,
			mountMetricKey,
			getSpotlightForCell,
			TableMaskView,
			dataProvider,
			firstColumnHeadingAlignment,
			lastColumnHeadingAlignment,
			intl: { formatMessage },
			tableLabel,
			resultsAnnouncerMessage,
		} = this.props;

		const headCells: HeadCellType[] = tableConfiguration.map(
			({
				title,
				width,
				sortField: cellSortField,
				dataTestId,
				isIconOnlyHeader,
				ascendingSortTooltip,
				descendingSortTooltip,
			}): HeadCellType => {
				let content = '';
				const wrapperProps = dataTestId ? { 'data-test-id': dataTestId } : {};

				if (dataTestId) {
					// @ts-expect-error - TS2322 - Type 'Element' is not assignable to type 'string'.
					content = <NonBreakingCell {...wrapperProps} />;
				}

				if (typeof title === 'function') {
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					const HeaderCell: ComponentType<Record<any, any>> = title;
					// @ts-expect-error - TS2322 - Type 'Element' is not assignable to type 'string'.
					content = (() => {
						if (!isVisualRefreshEnabled()) {
							return (
								<HeaderWrapper {...wrapperProps}>
									<HeaderCell />
								</HeaderWrapper>
							);
						}

						return (
							<Box xcss={styles.headerWrapper} testId={dataTestId}>
								<HeaderCell />
							</Box>
						);
					})();
				} else if (typeof title !== 'undefined') {
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
					const message: MessageDescriptor = title as any;

					// @ts-expect-error - TS2322 - Type 'Element' is not assignable to type 'string'.
					content = isVisualRefreshEnabled() ? (
						<Flex>
							<Text
								testId={dataTestId}
								color="color.text.subtle"
								maxLines={1}
								weight="bold"
								size="small"
							>
								{formatMessage(message)}
							</Text>
						</Flex>
					) : (
						<NonBreakingCell {...wrapperProps}>{formatMessage(message)}</NonBreakingCell>
					);
				}
				return {
					key: cellSortField || generateUniqId(),
					isSortable: !!cellSortField,
					content,
					width,
					isIconOnlyHeader: isVisualRefreshEnabled() ? !!isIconOnlyHeader : undefined,
					ascendingSortTooltip:
						ascendingSortTooltip && isVisualRefreshEnabled()
							? formatMessage(ascendingSortTooltip)
							: undefined,
					descendingSortTooltip:
						descendingSortTooltip && isVisualRefreshEnabled()
							? formatMessage(descendingSortTooltip)
							: undefined,
				};
			},
		);
		const head: HeadType = { cells: headCells };

		const ids = dataProvider
			? dataProvider.getData().map((entity) => dataProvider.idSelector(entity))
			: entityIds;

		const tableAriaLabel = tableLabel ? formatMessage(tableLabel) : undefined;

		const rowCells = ids.map((entityId, rowIndex) => {
			// the page is already sorted (by the API), so fake key,
			// so that DynamicTable won't destroy it
			const key = sortDirection === 'DESC' ? -(rowIndex + 1) : rowIndex + 1;
			const entity = dataProvider ? dataProvider.getData()[rowIndex] : undefined;
			const cells = tableConfiguration.map(({ Cell }, colIndex) => {
				const content = (
					<Cell
						id={entityId}
						entity={entity}
						isOperationInProgress={!!operationInitializationStatusPerEntity[entityId]}
						// eslint-disable-next-line jira/react-no-inline-function-prop
						onOperationRequested={(
							operation: TOperation,
							operationParams?: OperationParams<TChildEntityId>,
						) =>
							this.props.onOperationRequested({
								id: entityId,
								childEntityId: operationParams && operationParams.childEntityId,
								operation,
							})
						}
					/>
				);

				const getVisuallyRefreshedContent = (visuallyRefreshedContent: ReactNode) => {
					return visuallyRefreshedContent;
				};

				const cellMatch: SpotlightCellTarget = getSpotlightForCell(entityId, colIndex);

				if (cellMatch && cellMatch.spotlightId) {
					const onboardingContent = (
						<SpotlightTarget name={cellMatch.spotlightId}>
							{getVisuallyRefreshedContent(content)}
						</SpotlightTarget>
					);
					return {
						content: onboardingContent,
						key,
					};
				}

				return {
					content: getVisuallyRefreshedContent(content),
					key,
				};
			});
			return { cells };
		});

		return (
			// Note: the data-test-id attribute is used by Pollinator checks in Jalapeno so avoid changing if possible
			<Container
				data-test-id="global-pages.directories.directory-base.content.table.container"
				firstColumnHeadingAlignment={firstColumnHeadingAlignment}
				lastColumnHeadingAlignment={lastColumnHeadingAlignment}
				visualRefreshEnabled={isVisualRefreshEnabled()}
				visuallyRefreshedHeight={isVisualRefreshEnabled()}
			>
				{!isLoading && <MountReporter performanceMarkKey={mountMetricKey} />}
				<AnnouncerV2
					message={formatMessage(resultsAnnouncerMessage || messages.filterResultsAnnouncement, {
						itemCount: totalItems,
					})}
					shouldAnnounce={!isLoading && this.state.shouldAnnounceResults}
				/>
				<DynamicTableStateless
					isFixedSize
					isLoading={isLoading}
					head={head}
					rows={rowCells}
					onSort={this.onSort}
					sortKey={sortField}
					sortOrder={sortDirection}
					emptyView={isLoading ? <EmptyLoadingView /> : <EmptyView />}
					label={tableAriaLabel}
				/>
				{TableMaskView && <TableMaskView />}
			</Container>
		);
	}
}

// @ts-expect-error - Argument of type 'typeof Table' is not assignable to parameter of type 'ComponentType<WithIntlProvided<Props<unknown, string, unknown, unknown>>> & typeof Table'.
export default injectIntl(Table);

const TABLE_ROW_HEIGHT = '40px';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NonBreakingCell = styled2.span({
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled2.div<{
	firstColumnHeadingAlignment: ColumnAlignment;
	lastColumnHeadingAlignment: ColumnAlignment;
	visualRefreshEnabled?: boolean;
	visuallyRefreshedHeight?: boolean;
}>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) => ({
		position: 'relative',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& tbody': {
			verticalAlign: 'top',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& td': {
			paddingTop: 0,
			paddingBottom: 0,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'& th:first-of-type': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			textAlign: props.firstColumnHeadingAlignment,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			svg: {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
				height: props.visuallyRefreshedHeight ? '16px' : '14px',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
				width: props.visualRefreshEnabled ? '16px' : '14px',
			},
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'& th:last-of-type': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			textAlign: props.lastColumnHeadingAlignment,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& th': {
			verticalAlign: 'middle',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			span: {
				whiteSpace: 'normal',
			},
		},
	}),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ visualRefreshEnabled }) =>
		visualRefreshEnabled && {
			'& table': {
				borderWidth: token('border.width'),
				borderStyle: 'solid',
				borderColor: token('color.border'),
				borderRadius: '6px',
			},
			'& th': {
				font: token('font.heading.xxsmall'),
				backgroundColor: token('elevation.surface.sunken'),
				height: TABLE_ROW_HEIGHT,
				// Overriding the default border
				borderBlockEnd: `1px solid ${token('color.border')}`,
			},
			'& th:first-of-type': {
				borderTopLeftRadius: '6px',
			},
			'& th:last-of-type': {
				borderTopRightRadius: '6px',
			},
			'& tr:last-of-type td': {
				borderBlockEnd: 'none',
			},
			'& td': {
				borderBlockEnd: `1px solid ${token('color.border')}`,
			},
		},
);

const styles = cssMap({
	headerWrapper: {
		paddingLeft: token('space.100'),
		paddingRight: token('space.050'),
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
	},
	headerWrapperOld: {
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		paddingInline: token('space.050'),
		paddingBlock: token('space.100'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmptyLoadingView = styled2.div({
	height: '150px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderWrapper = styled2.div({
	display: 'inline-block',
});
